import React, { useEffect, useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Grid from '@material-ui/core/Grid';
import ChannelPreview from './ChannelPreview';
import CreateChannelDrawer from './CreateChannelDrawer';
import DataGrid from '@afosto/app-components-react/DataGrid';
import EditChannelDrawer from '../../components/EditChannelDrawer';
import Page from '@afosto/app-components-react/Page';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import { channels as channelsApi } from '@afosto/catalog/api/Channels';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import { channelFormatter, clientFormatter } from './formatters';
import translations from './translations';

const fetchChannels = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => channelsApi.listChannels({ isDeleted: 0 }, requestOptions),
    {
      transformData: data => data,
    },
  );

const Channels = () => {
  usePrivate();

  const history = useHistory();
  const confirm = useConfirm();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['cat:channel:read']);
  const [channel, setChannel] = useState(null);
  const [isLoadingChannel, setIsLoadingChannel] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showEditChannel, setShowEditChannel] = useState(false);

  const dataSource = useDataGrid(fetchChannels, {
    modules: ['dataProperties', 'pagination', 'activeItem'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.channels.name',
            defaultMessage: 'Name',
            description: 'The column name for column name',
          }),
          type: 'channel',
        },
        {
          key: 'description',
          name: intl.formatMessage({
            id: 'dataGrid.columns.channels.description',
            defaultMessage: 'Description',
            description: 'The column name for column description',
          }),
        },
        {
          key: 'type',
          name: intl.formatMessage({
            id: 'dataGrid.columns.channels.type',
            defaultMessage: 'Type',
            description: 'The column name for column type',
          }),
        },
        {
          key: 'client_id',
          name: intl.formatMessage({
            id: 'dataGrid.columns.channels.client',
            defaultMessage: 'Client',
            description: 'The column name for column client id',
          }),
          type: 'client',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.channels.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
        {
          key: 'updated_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.channels.updatedAt',
            defaultMessage: 'Updated at',
            description: 'The column name for column updated at',
          }),
          type: 'datetime',
        },
      ],
    },
  });
  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem } = activeItemModule;

  useEffect(() => {
    let isMounted = true;

    const fetchChannel = async id => {
      try {
        setIsLoadingChannel(true);

        const response = await channelsApi.viewChannel(id);
        const { data = null } = response;

        let clientData = null;

        if (data?.client_id) {
          const clientResponse = await oauthApi.getClient(data.client_id).catch(() => ({}));
          const { data: client } = clientResponse ?? {};

          clientData = client
            ? {
                ...client,
                label: client?.name,
                value: client?.client_id,
              }
            : null;
        }

        if (isMounted) {
          setChannel(data ? { ...data, client: clientData } : null);
        }
      } catch (err) {
        if (isMounted) {
          setChannel(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingChannel(false);
        }
      }
    };

    if (activeItem) {
      fetchChannel(activeItem);
    } else if (isMounted) {
      setChannel(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  const handleCloseChannelPreview = () => {
    dataSource.activeItem.setActiveItem(null);
    setChannel(null);
  };

  const handleCreateChannel = () => {
    setShowCreateChannel(false);
    dataSource.reloadDataSource();
  };

  const handleEditChannel = updatedChannel => {
    setShowEditChannel(false);
    setChannel(updatedChannel);
    dataSource.reloadDataSource();
  };

  const handleDeleteChannel = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.feedback.confirmDeleteChannel.title),
        description: intl.formatMessage(translations.feedback.confirmDeleteChannel.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.feedback.confirmDeleteChannel.actions.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.feedback.confirmDeleteChannel.actions.delete),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse !== 'delete') {
        return;
      }

      await channelsApi.deleteChannel(channel.id);

      enqueueSnackbar(intl.formatMessage(translations.feedback.channelDeleted), {
        variant: 'success',
      });

      dataSource.activeItem.setActiveItem(null);
      setChannel(null);
      dataSource.reloadDataSource();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.channelNotDeleted), {
        variant: 'error',
      });
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  const menu = [
    ...(channel
      ? [
          {
            key: 'channel_view',
            translationKey: 'actions.view',
            label: 'View',
            icon: 'eye',
            shortcut: true,
            onClick: () => history.push(`/channels/${channel.id}`),
          },
          ...(hasPermissions(['cat:channel:write'])
            ? [
                {
                  key: 'channel_edit',
                  translationKey: 'actions.edit',
                  label: 'Edit',
                  icon: 'pencil',
                  shortcut: true,
                  onClick: () => setShowEditChannel(true),
                },
                {
                  key: 'channel_delete',
                  translationKey: 'app.users.preview.delete',
                  label: 'Delete',
                  icon: 'trash-alt',
                  color: 'secondary',
                  onClick: handleDeleteChannel,
                },
              ]
            : []),
        ]
      : []),
  ];

  return (
    <Grid container wrap="nowrap">
      <Page
        title={intl.formatMessage(translations.title)}
        fullWidth
        style={{ flexGrow: 1 }}
        primaryAction={
          hasPermissions(['cat:channel:write'])
            ? {
                text: intl.formatMessage(translations.create.button),
                action: () => setShowCreateChannel(true),
              }
            : null
        }
      >
        <DataGrid
          dataSource={dataSource}
          formatters={{ channel: channelFormatter, client: clientFormatter }}
        />
      </Page>
      <ChannelPreview
        channel={channel}
        isLoading={isLoadingChannel}
        isOpen={!!channel}
        menu={menu}
        onClose={handleCloseChannelPreview}
      />
      <EditChannelDrawer
        channel={channel}
        isOpen={showEditChannel}
        onClose={() => setShowEditChannel(false)}
        onSave={handleEditChannel}
      />
      <CreateChannelDrawer
        isOpen={showCreateChannel}
        onClose={() => setShowCreateChannel(false)}
        onCreate={handleCreateChannel}
      />
    </Grid>
  );
};

export default Channels;
